import { useState, useMemo } from 'react'
import { Loader2, AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { useParty } from '@/hooks/useParty'
import { usePartiesByNation } from '@/hooks/usePartiesByNation'
import { useDistrictsWithRegionByNation } from '@/hooks/useDistrictsWithRegionByNation'
import { useCreateAction } from '@/hooks/useCreateAction'
import { cn } from '@/lib/utils'

interface AttackActionFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  partyId: string
  currentTick: number
  availableAP: number
  onSuccess?: () => void
  onBack?: () => void
}

type AttackType = 'negative_ads' | 'opposition_research'
type AttackScope = 'national' | 'district'

const ATTACK_TYPES = {
  negative_ads: {
    label: 'Negative Ads',
    icon: '📺',
    description: 'Direct attack advertising against a rival party',
    detailedDescription:
      'Reduces target party\'s support in the targeted area. Has backfire risk if voters perceive the ads as unfair.',
    warningText:
      'Attack ads may backfire if voters perceive them as unfair, damaging your party\'s reputation.',
  },
  opposition_research: {
    label: 'Opposition Research',
    icon: '🔍',
    description: 'Investigate rival party for scandals',
    detailedDescription:
      'Chance to uncover damaging information about target party or their candidates. Higher investment = better odds.',
    warningText:
      'Research may find nothing. If a scandal is discovered, your involvement remains hidden unless uncovered by counter-intelligence.',
  },
} as const

const INTENSITY_DESCRIPTIONS: Record<number, string> = {
  2: 'Light probe',
  3: 'Moderate effort',
  4: 'Serious campaign',
  5: 'Full offensive',
}

function getResearchChanceLabel(intensity: number): string {
  if (intensity <= 2) return 'Low'
  if (intensity <= 3) return 'Medium'
  if (intensity <= 4) return 'Medium-High'
  return 'High'
}

function getExpectedImpactRange(
  intensity: number,
  isNational: boolean
): { min: number; max: number } {
  // Base effect range depends on intensity
  const baseMin = intensity * 1.5
  const baseMax = intensity * 3
  // National is less effective per-district but affects more areas
  const multiplier = isNational ? 0.6 : 1.0
  return {
    min: Math.round(baseMin * multiplier * 10) / 10,
    max: Math.round(baseMax * multiplier * 10) / 10,
  }
}

export function AttackActionForm({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: AttackActionFormProps) {
  if (!open) return null

  return (
    <AttackActionFormContent
      open={open}
      onOpenChange={onOpenChange}
      partyId={partyId}
      currentTick={currentTick}
      availableAP={availableAP}
      onSuccess={onSuccess}
      onBack={onBack}
    />
  )
}

function AttackActionFormContent({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: AttackActionFormProps) {
  // Form state
  const [targetPartyId, setTargetPartyId] = useState<string>('')
  const [attackType, setAttackType] = useState<AttackType>('negative_ads')
  const [scope, setScope] = useState<AttackScope>('national')
  const [districtId, setDistrictId] = useState<string>('')
  const [intensity, setIntensity] = useState(2)

  // Fetch current party to get nation_id
  const { party, isLoading: partyLoading } = useParty(partyId)

  // Fetch all parties in the nation
  const { parties, isLoading: partiesLoading } = usePartiesByNation(party?.nation_id)

  // Fetch districts for the nation (for scope selection)
  const { districts, isLoading: districtsLoading } = useDistrictsWithRegionByNation(
    party?.nation_id
  )

  // Create action mutation
  const { createAction, isCreating, error } = useCreateAction({
    onSuccess: () => {
      onSuccess?.()
      onOpenChange(false)
    },
  })

  // Filter out the player's party to get rivals
  const rivalParties = useMemo(() => {
    if (!parties) return []
    return parties.filter((p) => p.id !== partyId && p.is_active)
  }, [parties, partyId])

  // Get selected target party info
  const selectedTargetParty = useMemo(() => {
    if (!targetPartyId || !rivalParties) return null
    return rivalParties.find((p) => p.id === targetPartyId)
  }, [targetPartyId, rivalParties])

  // Get selected district info
  const selectedDistrict = useMemo(() => {
    if (scope !== 'district' || !districtId || !districts) return null
    return districts.find((d) => d.id === districtId)
  }, [scope, districtId, districts])

  // Calculate effect estimates
  const effectEstimate = useMemo(() => {
    if (attackType === 'negative_ads') {
      const impact = getExpectedImpactRange(intensity, scope === 'national')
      return {
        type: 'impact' as const,
        min: impact.min,
        max: impact.max,
        scope: scope === 'national' ? 'all districts' : selectedDistrict?.name ?? 'selected district',
      }
    } else {
      return {
        type: 'research' as const,
        chanceLabel: getResearchChanceLabel(intensity),
      }
    }
  }, [attackType, intensity, scope, selectedDistrict])

  // Validation
  const canSubmit =
    !isCreating &&
    targetPartyId !== '' &&
    intensity <= availableAP &&
    intensity >= 2 &&
    (attackType === 'opposition_research' || scope === 'national' || districtId !== '')

  const apAfterAction = availableAP - intensity
  const minAP = 2
  const maxAP = Math.min(5, availableAP)

  const handleSubmit = () => {
    if (!canSubmit) return

    createAction({
      party_id: partyId,
      tick: currentTick,
      action_type: 'attack',
      action_subtype: attackType,
      target_type: 'party',
      target_id: targetPartyId,
      action_points_spent: intensity,
      parameters: {
        scope: attackType === 'negative_ads' ? scope : undefined,
        district_id: attackType === 'negative_ads' && scope === 'district' ? districtId : undefined,
        target_party_name: selectedTargetParty?.name,
        target_district_name: selectedDistrict?.name,
      },
    })
  }

  const handleBack = () => {
    onBack?.()
    onOpenChange(false)
  }

  const isLoading = partyLoading || partiesLoading

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>⚔️</span> Configure Attack
          </DialogTitle>
          <DialogDescription>
            Launch a negative campaign against a rival party. Choose your target and
            approach carefully.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Target Party Selection */}
          <div className="space-y-2">
            <Label htmlFor="target-party">Target Party</Label>
            {isLoading ? (
              <div className="flex h-9 items-center justify-center">
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
              </div>
            ) : rivalParties.length === 0 ? (
              <p className="text-muted-foreground text-sm">No rival parties found</p>
            ) : (
              <Select value={targetPartyId} onValueChange={setTargetPartyId}>
                <SelectTrigger id="target-party">
                  <SelectValue placeholder="Select target party..." />
                </SelectTrigger>
                <SelectContent>
                  {rivalParties.map((party) => (
                    <SelectItem key={party.id} value={party.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: party.color }}
                        />
                        <span>
                          {party.name} ({party.abbreviation})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Attack Type Selection */}
          <div className="space-y-3">
            <Label>Attack Type</Label>
            <div className="grid grid-cols-2 gap-3">
              {(Object.entries(ATTACK_TYPES) as [AttackType, typeof ATTACK_TYPES[AttackType]][]).map(
                ([type, config]) => {
                  const isSelected = attackType === type
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        setAttackType(type)
                        // Reset scope when switching types
                        if (type === 'opposition_research') {
                          setScope('national')
                          setDistrictId('')
                        }
                      }}
                      className={cn(
                        'rounded-lg border-2 p-3 text-left transition-all',
                        'hover:border-primary/50 hover:bg-accent/50',
                        isSelected
                          ? 'border-primary bg-accent'
                          : 'border-border bg-background'
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{config.icon}</span>
                          <span className="font-medium">{config.label}</span>
                        </div>
                        {isSelected && (
                          <div className="bg-primary text-primary-foreground rounded-full p-0.5">
                            <svg
                              className="h-3 w-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {config.description}
                      </p>
                    </button>
                  )
                }
              )}
            </div>
          </div>

          {/* Scope Selection (only for negative_ads) */}
          {attackType === 'negative_ads' && (
            <div className="space-y-2">
              <Label>Scope</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={scope === 'national' ? 'default' : 'outline'}
                  className="w-full"
                  onClick={() => {
                    setScope('national')
                    setDistrictId('')
                  }}
                >
                  National
                </Button>
                <Button
                  type="button"
                  variant={scope === 'district' ? 'default' : 'outline'}
                  className="w-full"
                  onClick={() => setScope('district')}
                >
                  District
                </Button>
              </div>
              <p className="text-muted-foreground text-xs">
                {scope === 'national'
                  ? 'Attack ads will run nationwide (less impact per district, wider reach)'
                  : 'Focus attack on a single district (more concentrated impact)'}
              </p>
            </div>
          )}

          {/* District Selector (only for negative_ads with district scope) */}
          {attackType === 'negative_ads' && scope === 'district' && (
            <div className="space-y-2">
              <Label htmlFor="district-select">Select District</Label>
              {districtsLoading ? (
                <div className="flex h-9 items-center justify-center">
                  <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                </div>
              ) : (
                <Select value={districtId} onValueChange={setDistrictId}>
                  <SelectTrigger id="district-select">
                    <SelectValue placeholder="Choose a district..." />
                  </SelectTrigger>
                  <SelectContent>
                    {districts?.map((district) => (
                      <SelectItem key={district.id} value={district.id}>
                        {district.name} ({district.region.name})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Intensity Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Intensity</Label>
              <span className="text-sm font-medium">{intensity} AP</span>
            </div>
            <Slider
              value={[intensity]}
              onValueChange={([value]) => setIntensity(value)}
              min={minAP}
              max={maxAP}
              step={1}
              disabled={availableAP < minAP}
            />
            <div className="text-muted-foreground flex justify-between text-xs">
              <span>2 - Light</span>
              <span>3-4 - Moderate</span>
              <span>5 - Full</span>
            </div>
            <p className="text-muted-foreground text-xs">
              {INTENSITY_DESCRIPTIONS[intensity] ?? 'Moderate effort'}
            </p>
          </div>

          {/* Risk Warning */}
          <div className="bg-amber-500/10 border-amber-500/50 flex items-start gap-3 rounded-lg border p-3">
            <AlertTriangle className="text-amber-500 mt-0.5 h-5 w-5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-amber-700 dark:text-amber-400">
                Risk Warning
              </p>
              <p className="text-muted-foreground mt-1 text-xs">
                {ATTACK_TYPES[attackType].warningText}
              </p>
            </div>
          </div>

          {/* Effect Preview */}
          {selectedTargetParty && (
            <div className="bg-muted/50 space-y-2 rounded-lg border p-4">
              <h4 className="text-sm font-medium">Expected Effect</h4>
              <div className="space-y-1 text-sm">
                {effectEstimate.type === 'impact' ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Target:</span>
                      <span className="font-medium">
                        <span
                          className="mr-1 inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: selectedTargetParty.color }}
                        />
                        {selectedTargetParty.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Expected impact:</span>
                      <span className="font-medium text-red-600">
                        -{effectEstimate.min}% to -{effectEstimate.max}% support
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Affected area:</span>
                      <span className="font-medium">{effectEstimate.scope}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Target:</span>
                      <span className="font-medium">
                        <span
                          className="mr-1 inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: selectedTargetParty.color }}
                        />
                        {selectedTargetParty.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Chance of finding scandal:</span>
                      <span
                        className={cn(
                          'font-medium',
                          effectEstimate.chanceLabel === 'Low'
                            ? 'text-orange-500'
                            : effectEstimate.chanceLabel === 'Medium'
                              ? 'text-yellow-600'
                              : 'text-green-600'
                        )}
                      >
                        {effectEstimate.chanceLabel}
                      </span>
                    </div>
                    <p className="text-muted-foreground pt-1 text-xs">
                      Success also depends on target candidates' scandal_prone trait.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* AP Summary */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">AP after action:</span>
            <span className={cn('font-medium', apAfterAction < 0 && 'text-destructive')}>
              {availableAP} → {Math.max(0, apAfterAction)}
            </span>
          </div>

          {/* Insufficient AP warning */}
          {availableAP < minAP && (
            <p className="text-destructive text-sm">
              Attacks require at least {minAP} AP. You have {availableAP} AP available.
            </p>
          )}

          {/* Error display */}
          {error && (
            <p className="text-destructive text-sm">
              {error.message || 'Failed to queue action'}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleBack} disabled={isCreating}>
            Back
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isCreating ? 'Queuing...' : 'Queue Attack'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
